/**
 * Kratos 服务层 - 使用官方 @ory/kratos-client SDK
 * 参考 OneAuth 实现: D:\Work\h2os.cloud\one-auth\web\src\services\kratos.ts
 */
import { Configuration, FrontendApi } from '@ory/kratos-client';
import axios from 'axios';

/**
 * CSRF Token 管理
 * 从 Kratos flow 响应中自动提取并缓存 CSRF token
 */
const csrfTokenCache = new Map();

// 从 flow UI 节点中提取 CSRF token
function extractCsrfToken(flow) {
  if (!flow?.ui?.nodes) return '';

  const csrfNode = flow.ui.nodes.find(
    (node) => node.attributes?.name === 'csrf_token'
  );

  return csrfNode?.attributes?.value || '';
}

// 缓存 flow 的 CSRF token
function cacheFlowCsrfToken(flowId, flow) {
  const token = extractCsrfToken(flow);
  if (token && flowId) {
    csrfTokenCache.set(flowId, token);
    console.log(`🔑 [kratosService] 缓存 CSRF token: ${flowId.substring(0, 8)}...`);
  }
  return token;
}

// 获取缓存的 CSRF token
function getCachedCsrfToken(flowId) {
  return csrfTokenCache.get(flowId) || '';
}

// 清除缓存的 CSRF token
function clearCsrfTokenCache(flowId) {
  csrfTokenCache.delete(flowId);
}

// 创建自定义 axios 实例
const axiosInstance = axios.create({
  baseURL: '',
  withCredentials: true, // 重要：携带 Cookie
});

// 请求拦截器：自动添加 CSRF token
axiosInstance.interceptors.request.use(
  (config) => {
    console.log('🔧 SDK 拦截器 - 原始请求:', {
      method: config.method,
      url: config.url,
      data: config.data,
    });

    // 只对 POST/PUT/PATCH/DELETE 请求自动添加 CSRF token
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
      // 从 URL 参数中提取 flow ID
      const url = new URL(config.url || '', window.location.origin);
      const flowId = url.searchParams.get('flow');

      if (flowId) {
        const cachedToken = getCachedCsrfToken(flowId);
        console.log('🔧 SDK 拦截器 - CSRF Token:', cachedToken ? `${cachedToken.substring(0, 10)}...` : 'none');

        // 如果缓存中有 token，且请求体中没有 csrf_token，则自动添加
        if (cachedToken && config.data) {
          if (typeof config.data === 'string') {
            // 处理字符串形式的 JSON
            try {
              const dataObj = JSON.parse(config.data);
              if (!dataObj.csrf_token) {
                dataObj.csrf_token = cachedToken;
                config.data = JSON.stringify(dataObj);
                console.log('✅ SDK 拦截器 - 添加 CSRF 后:', dataObj);
              }
            } catch (e) {
              console.warn('Failed to parse request data:', e);
            }
          } else if (typeof config.data === 'object' && !config.data.csrf_token) {
            config.data.csrf_token = cachedToken;
            console.log('✅ SDK 拦截器 - 添加 CSRF 后:', config.data);
          }
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：自动缓存 CSRF token + 处理 400 响应
axiosInstance.interceptors.response.use(
  (response) => {
    // 如果响应包含 flow 数据，自动缓存其 CSRF token
    const flow = response.data;
    if (flow?.id && flow?.ui?.nodes) {
      cacheFlowCsrfToken(flow.id, flow);
    }

    return response;
  },
  (error) => {
    // 统一处理 Kratos 的 400 响应
    if (error.response?.status === 400) {
      const flowData = error.response?.data;

      console.log('🔍 SDK - Kratos 400 响应:', {
        url: error.config?.url,
        method: error.config?.method,
        state: flowData?.state,
        active: flowData?.active,
        hasUIMessages: !!flowData?.ui?.messages?.length,
      });

      // 如果响应包含 flow 数据，缓存其 CSRF token
      if (flowData?.id && flowData?.ui?.nodes) {
        cacheFlowCsrfToken(flowData.id, flowData);
      }

      // 检查是否有错误消息
      const hasErrors = flowData?.ui?.messages?.some((msg) => msg.type === 'error');
      if (hasErrors) {
        console.error('❌ SDK - Kratos 请求失败 (400 with errors)');
        return Promise.reject(error);
      }

      // 检查是否是验证码发送成功（没有错误消息）
      const isCodeSent = flowData?.state?.startsWith('sent_') ||
                         (flowData?.active === 'code' && flowData?.state);

      if (isCodeSent) {
        console.log('✅ SDK - 验证码已发送 (400 -> 200):', {
          state: flowData.state,
          active: flowData.active,
        });

        // 转换为成功的响应
        return Promise.resolve({
          ...error.response,
          status: 200,
          statusText: 'OK',
          data: flowData
        });
      }
    }

    console.error('❌ SDK 错误:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

// 创建 Kratos 配置
const configuration = new Configuration({
  basePath: '', // 使用相对路径，通过 Vite dev server 的代理访问
  baseOptions: {
    withCredentials: true,
  },
});

// 创建 FrontendApi 单例，使用自定义 axios 实例
const kratosClient = new FrontendApi(configuration, '', axiosInstance);

/**
 * ==================== 登录流程 ====================
 */

/**
 * 初始化登录流程
 * @param {string} returnTo - 登录成功后的返回 URL
 * @param {boolean} refresh - 是否刷新
 * @returns {Promise<Object>} LoginFlow 对象
 */
export async function initLoginFlow(returnTo = '/', refresh = false) {
  console.log('🌐 [kratosService/initLoginFlow] 调用 Kratos SDK', { returnTo, refresh });

  const response = await kratosClient.createBrowserLoginFlow({
    returnTo,
    refresh,
  });

  console.log('✅ [kratosService/initLoginFlow] Flow 创建成功:', {
    flowId: response.data?.id,
    status: response.status,
  });

  // 自动缓存 CSRF token
  if (response.data?.id) {
    cacheFlowCsrfToken(response.data.id, response.data);
  }

  return response.data;
}

/**
 * 提交密码登录
 * @param {string} flowId - Flow ID
 * @param {string} identifier - 用户名/邮箱/手机号
 * @param {string} password - 密码
 * @returns {Promise<Object>} 登录结果
 */
export async function submitLoginPassword(flowId, identifier, password) {
  console.log('🔐 [kratosService/submitLoginPassword] 提交密码登录:', { flowId: flowId.substring(0, 8) + '...' });

  const body = {
    method: 'password',
    identifier,
    password,
  };

  const response = await kratosClient.updateLoginFlow({
    flow: flowId,
    updateLoginFlowBody: body,
  });

  console.log('✅ [kratosService/submitLoginPassword] 登录成功');
  return response.data;
}

/**
 * 发送登录验证码
 * @param {string} flowId - Flow ID
 * @param {string} identifier - 邮箱或手机号
 * @returns {Promise<Object>} Flow 数据
 */
export async function sendLoginCode(flowId, identifier) {
  console.log('📱 [kratosService/sendLoginCode] 发送登录验证码:', {
    flowId: flowId.substring(0, 8) + '...',
    identifier,
  });

  const body = {
    method: 'code',
    identifier,
  };

  const response = await kratosClient.updateLoginFlow({
    flow: flowId,
    updateLoginFlowBody: body,
  });

  console.log('✅ [kratosService/sendLoginCode] 验证码已发送');

  // 缓存返回的 flow 的 CSRF token
  if (response.data?.id) {
    cacheFlowCsrfToken(response.data.id, response.data);
  }

  return response.data;
}

/**
 * 提交验证码登录
 * @param {string} flowId - Flow ID
 * @param {string} identifier - 邮箱或手机号
 * @param {string} code - 验证码
 * @returns {Promise<Object>} 登录结果
 */
export async function submitLoginCode(flowId, identifier, code) {
  console.log('🔐 [kratosService/submitLoginCode] 提交验证码登录:', {
    flowId: flowId.substring(0, 8) + '...',
    identifier,
  });

  const body = {
    method: 'code',
    identifier,
    code,
  };

  const response = await kratosClient.updateLoginFlow({
    flow: flowId,
    updateLoginFlowBody: body,
  });

  console.log('✅ [kratosService/submitLoginCode] 登录成功');
  return response.data;
}

/**
 * ==================== 注册流程 ====================
 */

/**
 * 初始化注册流程
 * @param {string} returnTo - 注册成功后的返回 URL
 * @returns {Promise<Object>} RegistrationFlow 对象
 */
export async function initRegistrationFlow(returnTo = '/') {
  console.log('🌐 [kratosService/initRegistrationFlow] 调用 Kratos SDK', { returnTo });

  const response = await kratosClient.createBrowserRegistrationFlow({
    returnTo,
  });

  console.log('✅ [kratosService/initRegistrationFlow] Flow 创建成功:', {
    flowId: response.data?.id,
    status: response.status,
  });

  // 自动缓存 CSRF token
  if (response.data?.id) {
    cacheFlowCsrfToken(response.data.id, response.data);
  }

  return response.data;
}

/**
 * 发送注册验证码
 * @param {string} flowId - Flow ID
 * @param {Object} traits - 用户信息 { email, phone, username, etc. }
 * @returns {Promise<Object>} Flow 数据
 */
export async function sendRegistrationCode(flowId, traits) {
  console.log('📱 [kratosService/sendRegistrationCode] 发送注册验证码:', {
    flowId: flowId.substring(0, 8) + '...',
    traits,
  });

  const body = {
    method: 'code',
    traits,
  };

  const response = await kratosClient.updateRegistrationFlow({
    flow: flowId,
    updateRegistrationFlowBody: body,
  });

  console.log('✅ [kratosService/sendRegistrationCode] 验证码已发送');

  // 缓存返回的 flow 的 CSRF token
  if (response.data?.id) {
    cacheFlowCsrfToken(response.data.id, response.data);
  }

  return response.data;
}

/**
 * 提交注册验证码
 * @param {string} flowId - Flow ID
 * @param {Object} traits - 用户信息
 * @param {string} code - 验证码
 * @param {string} password - 密码 (可选，取决于配置)
 * @returns {Promise<Object>} 注册结果
 */
export async function submitRegistrationCode(flowId, traits, code, password = null) {
  console.log('🔐 [kratosService/submitRegistrationCode] 提交注册验证码:', {
    flowId: flowId.substring(0, 8) + '...',
    traits,
    hasPassword: !!password,
  });

  const body = {
    method: 'code',
    traits,
    code,
  };

  // 如果提供了密码，也一起提交
  if (password) {
    body.password = password;
  }

  const response = await kratosClient.updateRegistrationFlow({
    flow: flowId,
    updateRegistrationFlowBody: body,
  });

  console.log('✅ [kratosService/submitRegistrationCode] 注册成功');
  return response.data;
}

/**
 * ==================== Session 管理 ====================
 */

/**
 * 获取当前 Session
 * @returns {Promise<Object>} Session 对象
 */
export async function getSession() {
  console.log('🌐 [kratosService/getSession] 获取当前 Session');

  try {
    const response = await kratosClient.toSession();
    console.log('✅ [kratosService/getSession] Session 获取成功:', response.data?.id);
    return response.data;
  } catch (error) {
    console.log('❌ [kratosService/getSession] 未找到有效 Session');
    throw error;
  }
}

/**
 * 检查用户是否已登录（有有效的 Kratos session）
 * @returns {Promise<boolean>} 是否已登录
 */
export async function isLoggedIn() {
  try {
    await getSession();
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * ==================== 工具函数 ====================
 */

/**
 * 从 Flow 响应中提取错误消息
 * @param {Object} flowData - Flow 响应数据
 * @returns {Array} 错误消息数组
 */
export function extractErrors(flowData) {
  const errors = [];

  if (!flowData?.ui?.messages) {
    return errors;
  }

  // 提取全局错误消息
  flowData.ui.messages.forEach((msg) => {
    if (msg.type === 'error') {
      errors.push({
        id: msg.id,
        text: msg.text,
        type: msg.type,
      });
    }
  });

  // 提取字段级别的错误消息
  if (flowData.ui.nodes) {
    flowData.ui.nodes.forEach((node) => {
      if (node.messages) {
        node.messages.forEach((msg) => {
          if (msg.type === 'error') {
            errors.push({
              id: msg.id,
              text: msg.text,
              type: msg.type,
              field: node.attributes?.name,
            });
          }
        });
      }
    });
  }

  return errors;
}

// 导出 Kratos 客户端（如果需要直接使用）
export { kratosClient };

export default {
  // Login
  initLoginFlow,
  submitLoginPassword,
  sendLoginCode,
  submitLoginCode,

  // Registration
  initRegistrationFlow,
  sendRegistrationCode,
  submitRegistrationCode,

  // Session
  getSession,

  // Utils
  extractErrors,
  getCachedCsrfToken,
  clearCsrfTokenCache,
};
